import { IdefaultExpandedRowContext } from "@/components/widgets/tradeActionSummary/ActionSummaryTableHelperFunctions";
import {
  onNodeCollapseChangeAllSettingsToFalse,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import {
  returnDealerValueRPatER,
  returnDealerValueRPatLeg,
} from "@/components/widgets/tradeActionSummary/helper/cellRenderers/DealerCellRenderer";
import { addDisabledButtonToStoreArray } from "@/components/widgets/tradeActionSummary/helper/cellRenderers/MainActionButtonActionSummary";
import { legHasReducedQuotedSize } from "@/components/widgets/tradeActionSummary/helper/colDefs/colDefsShared";
import {
  checkAllLegsAreQuotedDetailedResult,
  executeSynRepo,
  generateTradeJSON,
  quoteOutright,
  quoteStructure,
  quoteSynRepo,
  quoteValidationTwoWayOutright,
  quoteValidationTwoWayRepo,
  quoteValidationTwoWayStructureDirectionShown,
  quoteValidationTwoWayStructureDirectionWithheld,
} from "@/components/widgets/tradeActionSummary/helper/quoteAndExecuteFunctions/quoteFunctionAndValidation";
import {
  returnMissingQuoteCellsFromTradeJSON,
  returnMissingQuoteCellsFromLegs,
  returnBlocksWithMissingCash,
  returnPriceNotationBlocksQuotedAtZero,
  returnPriceNotationBlocksQuotedNegative,
  returnFUTBlocksQuotedAtZero,
  triggerValidationFlash,
  ER_LEVEL_FLASH_ID,
} from "@/components/widgets/tradeActionSummary/helper/quoteAndExecuteFunctions/quoteFlashHelper";
import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { TradeType } from "@/lib/interfaces and Enums/ENUMTradeType";
import { QuoteSide } from "@/lib/interfaces and Enums/ENUMQuoteSide";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { StructureTypeCode } from "@/lib/interfaces and Enums/ENUMStructureTypeCode";
import { IExecutionRequestCounterParties } from "@/lib/interfaces and Enums/InterfaceExecutionRequestCounterParties";
import { ExecutionRequestQuoteLeg } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import {
  generateMissingQuotesForQuotedSizeNotification,
  generateToastMessageForTradeFlowErrors,
  generateToastMessageTradeData,
} from "@/lib/notifications/toastNotificationHelpers";
import { fetchRequestWrapper } from "@/lib/utilities";
import { ICellRendererParams } from "ag-grid-community";
import { Mutex } from "async-mutex";
import { SetStateAction } from "react";
import { toast } from "react-toastify";
import tradeDataStore from "./tradeDataStore";
import { addAllocationsToHistoricalSearches } from "./historicalSearchStoreACTIONS";
// Cycle (it imports addTradeToStore back): only ever call refreshUnassignedBlocks at runtime.
import { refreshUnassignedBlocks } from "./importBlocksAndAllocationsStoreACTIONS";
import { advanceEditBaselineFromCurrent } from "./tradeSettingsStoreACTIONS";
import { recordServerQuoteSnapshots } from "./quoteSnapshotStoreACTIONS";
import tradesInProcessStore from "./tradesInProcessStore";

export function setButtonBackToActive(esid: string) {
  const currentTradesInProcess = tradesInProcessStore.getState().tradesInProcess;

  const newArr = currentTradesInProcess.filter((trade) => trade != esid);
  tradesInProcessStore.getState().removeTradeInProcess(newArr);
}

export function addTradeToStore(incomingTrade: ExecutionStrategy) {
  const currentTradeData = tradeDataStore.getState().tradeData;

  const newTradeData = [incomingTrade, ...currentTradeData];
  tradeDataStore.getState().updateTradeData(newTradeData);
}

export function initializeFillsForWorkingOrders(
  trades: ExecutionStrategy[],
): ExecutionStrategy[] {
  return trades.map((trade) => {
    if (
      trade.status !== ExecutionStrategyStatus.Working ||
      trade.role !== "RP" ||
      !trade.legs
    ) {
      return trade;
    }

    let needsUpdate = false;
    const newLegs = trade.legs.map((leg) => {
      if (!leg.fills || leg.fills.length === 0) {
        needsUpdate = true;
        return {
          ...leg,
          fills: [
            {
              fillId: 1,
              extFillId: 1,
              fillRate: undefined,
              fillSize: leg.notionalLeg1,
              fillType: "New" as const,
              blockId: leg.blockId,
            },
          ],
        };
      }
      return leg;
    });

    return needsUpdate ? { ...trade, legs: newLegs } : trade;
  });
}

// interface ITradeSummaryReturnMessage {
//   actionedBy: string;
//   details: string;
//   esid: string;
//   owner: string;
//   status: string;
//   structureType: string;
//   type: string;
//   role?: string; // currently not on incoming message as of 31/5/24
//   latestActivity?: string; // currently not on incoming message as of 31/5/24
// }

// LQC-655: edit PUT now mirrors the new-trade POST shape. Per-CP settings fall
// back to the trade-level defaults via resolveSettingForCP in
// generateOutgoingTradeMessage — same "override if present, else global" pattern
// as a fresh send.
export interface IEditPanelMessage {
  esid: string;
  requestType?: string;
  expiryPeriod?: number;
  expiryPeriodType?: string;
  expiryType?: string;
  minimumSize?: number;
  counterparties: {
    counterparty: string;
    deskRestriction: string[] | undefined;
    level?: any;
    releaseCompetition?: boolean;
    releaseCover?: boolean;
    releaseCoverSpread?: string;
    releaseCounterparty?: boolean;
    quoteType?: string;
    releaseDirection?: boolean;
    autoAffirm?: boolean;
    releaseSize?: number;
    releaseAllocation?: boolean;
    releaseSecurity?: boolean;
    orderType?: string | null;
    referenceSpread?: number | string;
    referenceSpreadFraction?: string;
    legs?: any[];
  }[];
}

const mutex = new Mutex();

export async function getAllActionSummaryData(
  getAccessTokenSilently: Function,
) {
  await mutex.runExclusive(async () => {
    try {
      const actionSummaryResponse = await fetchRequestWrapper(
        { url: "/Trade/Execution/Summary" },
        getAccessTokenSilently,
      );

      if (actionSummaryResponse) {
        const dataWithFills = initializeFillsForWorkingOrders(
          actionSummaryResponse,
        );
        recordServerQuoteSnapshots(dataWithFills, true);
        tradeDataStore.getState().updateTradeData(dataWithFills);
        tradeDataStore.getState().setTradeDataHasReturnedTrue();
      }
    } finally {
      // Success or failure - the blotter drops its loading overlay either way.
      tradeDataStore.getState().setTradeDataLoadAttemptedTrue();
    }
  });
}

export async function updateActionSummaryForESID(
  esid: string,
  getAccessTokenSilently: Function,
) {
  const actionSummaryResponse = await fetchRequestWrapper(
    { url: "/Trade/Execution/Summary/" + esid },
    getAccessTokenSilently,
  );
  const dataWithFills = initializeFillsForWorkingOrders(actionSummaryResponse);

  const release = await mutex.acquire();
  try {
    const currentTradeData = tradeDataStore.getState().tradeData;
    const newTradeData = [];

    for (let index = 0; index < currentTradeData.length; index++) {
      const currentTrade = currentTradeData[index];
      const summaryResponse = dataWithFills.find(
        (t: any) => t.esid == currentTrade.esid,
      );
      if (summaryResponse != null) {
        newTradeData.push(summaryResponse);
      } else if (dataWithFills.length > 0) {
        newTradeData.push(currentTrade);
      } else if (currentTrade.esid !== esid) {
        newTradeData.push(currentTrade);
      }
    }

    const currentEsids = tradeDataStore.getState().eisdsToFlash;
    for (let index = 0; index < dataWithFills.length; index++) {
      const summaryResponse = dataWithFills[index];
      currentEsids.push(summaryResponse.esid);

      const currentTradeIndex = currentTradeData.findIndex(
        (t) => t.esid == summaryResponse.esid,
      );
      if (currentTradeIndex == -1) {
        newTradeData.unshift(summaryResponse);
      }
    }
    tradeDataStore.getState().updateEisdsToFlash(currentEsids);
    recordServerQuoteSnapshots(dataWithFills);
    tradeDataStore.getState().updateTradeData(newTradeData);
  } finally {
    release();
  }
}

export async function updateIndicativeMidForESID(
  esid: string,
  getAccessTokenSilently: Function,
) {
  const actionSummaryResponse = await fetchRequestWrapper(
    { url: "/Trade/Execution/Summary/" + esid },
    getAccessTokenSilently,
  );

  mutex.runExclusive(() => {
    if (actionSummaryResponse) {
      let modifiedTrade: ExecutionStrategy | undefined;
      let loopNum = 0;
      // check trade exists every 500ms, for max of 10 seconds (10 loops).
      const interval = setInterval(() => {
        loopNum++;
        const currentTradeData = tradeDataStore.getState().tradeData;
        modifiedTrade = currentTradeData.find((t) => t.esid === esid);
        if (modifiedTrade) {
          const newTradeData = currentTradeData.map((trade) =>
            trade.esid !== esid
              ? trade
              : {
                  ...trade,
                  indicativeBid: actionSummaryResponse[0].indicativeBid,
                  indicativeAsk: actionSummaryResponse[0].indicativeAsk,
                  indicativeMid: actionSummaryResponse[0].indicativeMid,
                },
          );
          // tradeDataStore.getState().updateEisdsToFlash(currentEsids);
          tradeDataStore.getState().updateTradeData(newTradeData);
          clearInterval(interval);
        } else if (loopNum >= 10) {
          clearInterval(interval);
        }
      }, 500);
    }
  });
}

// ------- ENDPOINT TO GET ALLOCATION DATA -------
export async function getAllocationData({
  esid,
  getAccessTokenSilently,
  role,
  originatingCounterparty,
}: {
  esid: string;
  getAccessTokenSilently: Function;
  role: "OP" | "RP";
  originatingCounterparty: string;
}) {
  const currentTradeData = tradeDataStore.getState().tradeData;

  const getAllocationsResponse: any[] =
    (await fetchRequestWrapper(
      { url: "/Trade/Execution/Allocations/" + esid },
      getAccessTokenSilently,
    )) ?? [];
  // adding a unique ID to each allocation as backend is not sending one.
  getAllocationsResponse.forEach((allocation: any, index: number) => {
    allocation.id = allocation.extStructureId + index;
    allocation.role = role;
    allocation.originatingCounterparty = originatingCounterparty;
  });

  const newTradeDataWithAllocations = currentTradeData.map((trade) => {
    return trade.esid !== esid
      ? trade
      : { ...trade, allocations: [...getAllocationsResponse] };
  });

  tradeDataStore.getState().updateTradeData(newTradeDataWithAllocations);
  addAllocationsToHistoricalSearches(esid, getAllocationsResponse);
  return getAllocationsResponse;
}

/// --- --- ENDPOINTS FOR TRADE STATUS --- --- ///
// List of actions that can happen on the ER in addition to the ES:
//     Recall
//     Update Level
//     ReSend
//     Award
//     RequestRequote
//     WorkPanel
//     UnawardAlert CP
//     Alert CP
//     Affirm
//     Request Change
//     Update Allocations
//     WorkMore
//     ReOpen
//     Cancel in OMS

//     -- NOT IN CUBE 2 ATM
//     Send Ticket
//     ReCoupon
//     Show Fills
//     Send Ticket
//     Review ReCoupon Fees

//     sendToPannelStoreAction occuring now in the store file.
//     /Trade/Execution/Send
//     [ESIDStatusActionValidationFilterFactory([ExecutionStrategyStatus.New])

export async function sendToPannelStoreAction({
  outgoingTradeMessage,
  getAccessTokenSilently,
}: {
  outgoingTradeMessage: any;
  getAccessTokenSilently: Function;
}) {
  // make an update at the store level just for the incoming data ESID.
  if (outgoingTradeMessage.counterparties.length == 0) {
    generateToastMessageTradeData({
      action: "sendToPannelStoreAction",
      result: "error",
    });
    setButtonBackToActive(outgoingTradeMessage.esid);
    return false;
  }
  // Reject orders with no Limit
  // if (
  //   (outgoingTradeMessage.requestType == "Order" &&
  //     outgoingTradeMessage.counterparties[0].orderType == "Limit") ||
  //   (outgoingTradeMessage.requestType == "Order" &&
  //     outgoingTradeMessage.counterparties[0].orderType == "Stop")
  // ) {
  //   if (outgoingTradeMessage.counterparties[0].block) {
  //   }
  //   // If the `Legs` option has been selected, should this perform some validation checks?
  // }

  addDisabledButtonToStoreArray(outgoingTradeMessage.esid);

  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Send",
      method: "POST",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (sendTradeResponse) {
    const mappedCPS = outgoingTradeMessage.counterparties.map(
      (cp: any) => cp.counterparty,
    );
    generateToastMessageTradeData({
      action: "sendToPannelStoreAction",
      result: "success",
      additionalData: mappedCPS,
    });
    setButtonBackToActive(outgoingTradeMessage.esid);
  } else {
    setButtonBackToActive(outgoingTradeMessage.esid);
  }
  return true;
}

export async function editPanelStoreAction({
  getAccessTokenSilently,
  outgoingMessage,
}: {
  getAccessTokenSilently: Function;
  outgoingMessage: IEditPanelMessage;
}) {
  if (outgoingMessage.counterparties.length == 0) {
    // Use the Recall All logic here otherwise the ES status won't be updated:
    recallStoreAction({
      data: outgoingMessage,
      getAccessTokenSilently: getAccessTokenSilently,
    });
    return;
  }

  const editPanelResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Edit",
      method: "PUT",
      objToSend: outgoingMessage,
    },
    getAccessTokenSilently,
  );
  if (editPanelResponse) {
    const mappedCPS = outgoingMessage.counterparties.map((cp: any) => cp);

    generateToastMessageTradeData({
      action: "editPanelStoreAction",
      result: "success",
      additionalData: mappedCPS,
    });

    // LQC-774: recently-sent edits flip from orange (pending) to green (saved)
    // by advancing the baseline to the current store state.
    advanceEditBaselineFromCurrent(outgoingMessage.esid);
  }
}

export interface reSendObject {
  esid: string;
  executionRequestId?: number;
}
// reSendStoreAction occuring now in the store file.
// /Trade/Execution/ReSend
// [ESIDStatusActionValidationFilterFactory([ExecutionStrategyStatus.Recalled, ExecutionStrategyStatus.ReOpened, ExecutionStrategyStatus.Rejected, ExecutionStrategyStatus.NotDealt])]

export async function reSendStoreAction({
  data,
  getAccessTokenSilently,

  executionRequestId,
}: {
  data: any;
  getAccessTokenSilently: Function;
  executionRequestId?: number;
}) {
  // Orders should not Re-Send at the ES level if there are multiple CPs.
  // If there is only 1 CP, allow the ES-level re-send.
  if (!executionRequestId && data.type === TradeType.Order) {
    const counterParties = data.executionRequestCounterParties?.filter(
      (cp: IExecutionRequestCounterParties) =>
        cp.status === ExecutionRequestStatus.Recalled,
    );
    if (counterParties && counterParties.length > 1) {
      toast.error(
        "Orders can only be sent to 1 counterparty. Use Edit Panel or right-click a single counterparty.",
      );
      setButtonBackToActive(data.esid);
      return;
    }
  }

  const outgoingTradeMessage: reSendObject = {
    esid: data.esid,
  };
  // Below condition adds ERID to run the backend code at the ER level.
  if (executionRequestId) {
    if (data.type === TradeType.Order) {
      const otherCPs = data.executionRequestCounterParties?.filter(
        (cp: IExecutionRequestCounterParties) =>
          cp.executionRequestId !== executionRequestId &&
          cp.status !== ExecutionRequestStatus.Recalled,
      );
      if (otherCPs && otherCPs.length > 0) {
        toast.error("Orders can only be sent to 1 counterparty");
        setButtonBackToActive(outgoingTradeMessage.esid);
        return;
      }
    }
    outgoingTradeMessage.executionRequestId = executionRequestId;
  }

  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/ReSend",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "reSendStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

export interface awardObject {
  esid: string;
  executionRequestId?: number;
}

// awardStoreAction occuring now in the store file.
// /Trade/Execution/Award
// [ESIDStatusActionValidationFilterFactory([ExecutionStrategyStatus.Quoted])]

export async function awardStoreAction({
  data,
  getAccessTokenSilently,
  executionRequestId,
}: {
  data: any;
  getAccessTokenSilently: Function;
  executionRequestId?: number;
}) {
  // OP Specific
  const cpAtOrder1 = data.executionRequestCounterParties.find(
    (cp: IExecutionRequestCounterParties) => cp.order == 1,
  );

  if (cpAtOrder1 || executionRequestId) {
    const outgoingTradeMessage: awardObject = {
      esid: data.esid,
      executionRequestId: executionRequestId // If a passed in erID - always use this as priority.
        ? executionRequestId
        : cpAtOrder1.executionRequestId,
    };
    // } else if (executionRequestId) {
    //   outgoingTradeMessage.executionRequestId = executionRequestId;
    // }
    const sendTradeResponse = await fetchRequestWrapper(
      {
        url: "/Trade/Execution/Award",
        method: "POST",
        objToSend: outgoingTradeMessage,
      },
      getAccessTokenSilently,
    );

    setButtonBackToActive(outgoingTradeMessage.esid);
    if (sendTradeResponse) {
      generateToastMessageTradeData({
        action: "awardStoreAction",
        result: "success",
        ESIDPassed: data.esid,
      });
      return;
    }
  }

  // generateToastMessageTradeData({
  //   action: "awardStoreAction",
  //   result: "success",
  //   ESIDPassed: data.esid,
  // });
  generateToastMessageTradeData({
    action: "awardStoreAction",
    result: "error",
    ESIDPassed: data.esid,
  });
  // toast.error("No counterparty to award the trade to.");
  setButtonBackToActive(data.esid);
  return;
}

export async function awardMultiStoreAction({
  rows,
  getAccessTokenSilently,
}: {
  rows: any;
  getAccessTokenSilently: Function;
}) {
  // OP Specific
  const awardObjects: awardObject[] = [];
  const outgoingTradeMessage = { values: awardObjects };
  rows.forEach((data: any) => {
    const cpAtOrder1 = data.executionRequestCounterParties.find(
      (cp: IExecutionRequestCounterParties) => cp.order == 1,
    );

    if (cpAtOrder1) {
      const model: awardObject = {
        esid: data.esid,
        executionRequestId: cpAtOrder1.executionRequestId,
      };

      outgoingTradeMessage.values.push(model);
    }
  });

  let sendTradeResponse;
  try {
    sendTradeResponse = await fetchRequestWrapper(
      {
        url: "/Trade/Execution/AwardMultiple",
        method: "POST",
        objToSend: outgoingTradeMessage,
      },
      getAccessTokenSilently,
    );
  } finally {
    // Multi payload has no top-level esid so the wrapper can't clear busy state.
    // Clear all selected rows, including ones skipped from the payload.
    rows.forEach((row: any) => setButtonBackToActive(row.esid));
  }

  const esids = awardObjects.map((a) => a.esid);
  const esidPassed = esids.join();

  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "awardStoreAction",
      result: "success",
      ESIDPassed: esidPassed,
    });
    return;
  }

  toast.error("No counterparty to award the trade to.");
  return;
}

// notDealtStoreAction occuring now in the store file.
// /Trade/Execution/NotDealt
// [ESIDStatusActionValidationFilterFactory([ExecutionStrategyStatus.Sent])]

export interface notDealtObject {
  esid: string;
}

export async function notDealtStoreAction({
  data,
  getAccessTokenSilently,
  expandedRowsContext,
  setExpandedRowsContext,
  setExpandedRowsContextMostRecentlyUpdated,
}: {
  data: any;
  getAccessTokenSilently: Function;
  expandedRowsContext?: IdefaultExpandedRowContext[];
  setExpandedRowsContext?: {
    (_value: SetStateAction<IdefaultExpandedRowContext[]>): void;
  };
  setExpandedRowsContextMostRecentlyUpdated?: Function | undefined;
}) {
  const outgoingTradeMessage: notDealtObject = {
    esid: data.esid,
  };
  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/NotDealt",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    if (expandedRowsContext && setExpandedRowsContext) {
      onNodeCollapseChangeAllSettingsToFalse(
        // Set the trade settings to no longer be showing the CP list.
        expandedRowsContext,
        setExpandedRowsContext,
        data.esid,
        setExpandedRowsContextMostRecentlyUpdated,
      );
    }

    generateToastMessageTradeData({
      action: "notDealtStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
}

export async function viewTradeAction({
  params,
}: {
  data: any;
  getAccessTokenSilently: Function;
  params: ICellRendererParams;
}) {
  params.node.setExpanded(true);

  setButtonBackToActive(params.data.esid);
}
// reOpenStoreAction occuring now in the store file.
// /Trade/Execution/Reopen
// [ESIDStatusActionValidationFilterFactory([ExecutionStrategyStatus.Closed])]

export interface reopenTradeObject {
  esid: string;
  executionRequestId?: number;
}

export async function reOpenStoreAction({
  data,
  getAccessTokenSilently,

  executionRequestId,
}: {
  data: any;
  getAccessTokenSilently: Function;

  executionRequestId?: number;
}) {
  const outgoingTradeMessage: reopenTradeObject = {
    esid: data.esid,
  };
  if (executionRequestId) {
    outgoingTradeMessage.executionRequestId = executionRequestId;
  }
  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Reopen",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "reOpenStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}
export interface acknowledgeObject {
  esid: string;
  executionRequestId: number;
}

// /Trade/Execution/Acknowledge
// [ESIDStatusActionValidationFilterFactory([ExecutionStrategyStatus.Sent, ExecutionStrategyStatus.ReSent])]

export async function acknowledgeStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: acknowledgeObject = {
    esid: data.esid,
    executionRequestId: data.executionRequestId,
  };

  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Acknowledge",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "acknowledgeStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}
// unawardStoreAction occuring now in the store file.
// /Trade/Execution/Recall
// [ESIDStatusActionValidationFilterFactory([ExecutionStrategyStatus.Awarded])]

export interface unawardObject {
  esid: string;
  executionRequestId?: number;
}

export async function unawardStoreAction({
  data,
  getAccessTokenSilently,

  executionRequestId,
}: {
  data: any;
  getAccessTokenSilently: Function;

  executionRequestId?: number;
}) {
  const outgoingTradeMessage: unawardObject = {
    esid: data.esid,
  };
  // The backend code is not looking for an ER ID as there can only be one CP at the awarded status, so only the ESID will be accepted.
  if (executionRequestId) {
    outgoingTradeMessage.executionRequestId = executionRequestId;
  }
  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Unaward",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "unawardStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }

  await setButtonBackToActive(data.esid);
}

// recallStoreAction occuring now in the store file.
// /Trade/Execution/Recall
// [ESIDStatusActionValidationFilterFactory([ExecutionStrategyStatus.Sent, ExecutionStrategyStatus.Quoted])]

export interface recallObject {
  esid: string;
  executionRequestId?: number;
}

export async function recallStoreAction({
  data,
  getAccessTokenSilently,

  executionRequestId,
}: {
  data: any;
  getAccessTokenSilently: Function;

  executionRequestId?: number;
}) {
  const outgoingTradeMessage: recallObject = {
    esid: data.esid,
  };
  // Below condition adds ERID to run the backend code at the ER level.
  if (executionRequestId) {
    outgoingTradeMessage.executionRequestId = executionRequestId;
  }

  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Recall",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "recallStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

// rejectTradeStoreAction occuring now in the store file.
// /Trade/Execution/Reject
// [ESIDStatusActionValidationFilterFactory([ExecutionStrategyStatus.Quoted, ExecutionStrategyStatus.Sent, ExecutionStrategyStatus.ReSent, ExecutionStrategyStatus.Acknowledged, ExecutionStrategyStatus.OffQuote, ExecutionStrategyStatus.QuoteExpired, ExecutionStrategyStatus.Working, ExecutionStrategyStatus.PartiallyFilled, ExecutionStrategyStatus.LevelUpdated, ExecutionStrategyStatus.LevelUpdatedCP, ExecutionStrategyStatus.LevelAccepted])]

export interface rejectObject {
  esid: string;
  executionRequestId: number;
}

export async function rejectStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: rejectObject = {
    esid: data.esid,
    executionRequestId: data.executionRequestId,
  };

  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Reject",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "rejectStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}
// /Trade/Execution/Affirm
// [ESIDStatusActionValidationFilterFactory([ExecutionStrategyStatus.Executed, ExecutionStrategyStatus.ExecutedOPChangeRequested, ExecutionStrategyStatus.ExecutedPendRPChangeRequested, ExecutionStrategyStatus.ExecutedRPAgreeChange, ExecutionStrategyStatus.FilledIncomplete, ExecutionStrategyStatus.Filled])]
export interface affirmObject {
  esid: string;
  executionRequestId?: number;
}

export async function affirmStoreAction({
  data,
  getAccessTokenSilently,
  executionRequestId,
}: {
  data: any;
  getAccessTokenSilently: Function;
  executionRequestId?: number;
}) {
  const outgoingTradeMessage: affirmObject = {
    esid: data.esid,
  };
  if (executionRequestId) {
    outgoingTradeMessage.executionRequestId = executionRequestId;
  }

  // let ercpToAffirm: IExecutionRequestCounterParties | undefined;
  // data.executionRequestCounterParties.forEach(
  //   (ercp: IExecutionRequestCounterParties) => {
  //     if (ercp.status && statusAtExecutedRange.includes(ercp.status)) {
  //       ercpToAffirm = ercp;
  //     }
  //   },
  // );

  // 14868 - Revert code related to "/Trade/Execution/Recoupon" and ReCoupon flow.
  // let isMissingTransactionFee = false;
  // if (ercpToAffirm && ercpToAffirm.legs) {
  //   ercpToAffirm.legs.forEach((leg: ExecutionRequestQuoteLeg) => {
  //     if (leg.unwind == "Recoupon") {
  //       if (leg.quoteSide == "Bid") {
  //         const feeObj = leg.fees?.find(
  //           (feeObj) => feeObj.transactionFeeSide == "Bid",
  //         );
  //         if (
  //           feeObj &&
  //           (feeObj.transactionFee == null ||
  //             feeObj.transactionFee == undefined ||
  //             feeObj.transactionFee == 0)
  //         ) {
  //           isMissingTransactionFee = true;
  //         }
  //       } else if (leg.quoteSide == "Ask") {
  //         const feeObj = leg.fees?.find(
  //           (feeObj) => feeObj.transactionFeeSide == "Ask",
  //         );
  //         if (
  //           feeObj &&
  //           (feeObj.transactionFee == null ||
  //             feeObj.transactionFee == undefined ||
  //             feeObj.transactionFee == 0)
  //         ) {
  //           isMissingTransactionFee = true;
  //         }
  //       }
  //     }
  //   });
  // }

  // if (isMissingTransactionFee) {
  //   generateToastMessageTradeData({
  //     action: "affirmStoreAction",
  //     result: "missingTransactionFee",
  //     ESIDPassed: data.esid,
  //   });
  // } else
  // {
  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Affirm",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "affirmStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  // }

  setButtonBackToActive(outgoingTradeMessage.esid);
}

// /Trade/Execution/MoveToAwardedOP
// ExecutedPendRPAgreeChange, ExecutedPendRPChangeRequested, ExecutedRPChangeRequested, ExecutedRPAgreeChange.

export interface moveToAwardedOPObject {
  esid: string;
  // executionRequestId: number;
  // No need for ERID as there will only be one tarde at any of the 4 status'.
}

export async function moveToAwardedOPStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: moveToAwardedOPObject = {
    esid: data.esid,
  };
  // No need for ERID as there will only be one tarde at any of the 4 status'.
  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/MoveToAwardedOP",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "moveToAwardedOPStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

// /Trade/Execution/OffQuote
// Quoted, ReQuoteRequested, Rejected, ReQuoteRequested, Awarded.

export interface offQuoteObject {
  esid: string;
  executionRequestId: number;
}

export async function offQuoteStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: offQuoteObject = {
    esid: data.esid,
    executionRequestId: data.executionRequestId,
  };

  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/OffQuote",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "offQuoteStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

// /Trade/Execution/ReQuote
// Quoted, OffQuote, QuoteExpired, ReQuoteRequested.

export interface reQuoteObject {
  esid: string;
  executionRequestId?: number;
}

export async function reQuoteStoreAction({
  data,
  getAccessTokenSilently,

  executionRequestId,
}: {
  data: any;
  getAccessTokenSilently: Function;

  executionRequestId?: number;
}) {
  const outgoingTradeMessage: reQuoteObject = {
    esid: data.esid,
  };
  if (executionRequestId) {
    outgoingTradeMessage.executionRequestId = executionRequestId;
  }
  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/ReQuote",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "reQuoteStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

// /Trade/Execution/MoveStatusBackOP
export interface moveStatusBackOPObject {
  esid: string;
  executionRequestId?: number;
}

export async function moveStatusBackOPStoreAction({
  data,
  getAccessTokenSilently,

  executionRequestId,
}: {
  data: any;
  getAccessTokenSilently: Function;

  executionRequestId?: number;
}) {
  const outgoingTradeMessage: moveStatusBackOPObject = {
    esid: data.esid,
  };
  if (executionRequestId) {
    outgoingTradeMessage.executionRequestId = executionRequestId;
  }
  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/MoveStatusBackOP",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "moveStatusBackOPStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

// /Trade/Execution/PullRequest
export interface pullRequestObject {
  esid: string;
  executionRequestId: number;
}

export async function pullRequestStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: pullRequestObject = {
    esid: data.esid,
    executionRequestId: data.executionRequestId,
  };

  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/PullRequest",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "pullRequestStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

// /Trade/Execution/MoveToAwardedRP
export interface agreeMoveToAwardedRPObject {
  esid: string;
  executionRequestId: number;
}

export async function agreeMoveToAwardedRPStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  // To be used with the RP changing status.
  const outgoingTradeMessage: agreeMoveToAwardedRPObject = {
    esid: data.esid,
    executionRequestId: data.executionRequestId,
  };

  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/MoveToAwardedRP",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "agreeMoveToAwardedRPStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

// /Trade/Execution/MoveStatusBackRP
export interface moveStatusBackRPObject {
  esid: string;
  executionRequestId: number;
}

export async function moveStatusBackRPStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: moveStatusBackRPObject = {
    esid: data.esid,
    executionRequestId: data.executionRequestId,
  };

  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/MoveStatusBackRP",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "moveStatusBackRPStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

// /Trade/Execution/AcceptLevel
export interface acceptLevelObject {
  esid: string;
}

export async function acceptLevelStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: acceptLevelObject = {
    esid: data.esid,
  };
  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/AcceptLevel",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "acceptLevelStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }

  setButtonBackToActive(outgoingTradeMessage.esid);

  return sendTradeResponse ? true : false;
}

//Setting a Closed trade that is at type Not Sent back to New.
export interface closedNotSentTradeObject {
  esid: string;
}
export async function setToNewStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: closedNotSentTradeObject = {
    esid: data.esid,
  };
  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/SetToNew",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (sendTradeResponse) {
    generateToastMessageTradeData({
      action: "setToNew",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

// API call for Executing - Called once validation is passed.
// NO need to update the button disabled state here as it needs to run regardless of the API outcome.
export async function executeTrade(
  getAccessTokenSilently: Function,
  outgoingTradeMessage: any,
) {
  const sendTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Execute",
      method: "POST",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  return sendTradeResponse ? true : false;
}

// 14868 - Revert code related to "/Trade/Execution/Recoupon" and ReCoupon flow.
// export async function recouponTrade(
//   getAccessTokenSilently: Function,
//   outgoingTradeMessage: any,
// ) {
//   const sendTradeResponse = await fetchRequestWrapper(
//     {
//       url: "/Trade/Execution/Recoupon",
//       method: "POST",
//       objToSend: outgoingTradeMessage,
//     },
//     getAccessTokenSilently,
//   );
//   return sendTradeResponse ? true : false;
// }

// API call for quoting - Called once validation is passed.
// NO need to update the button disabled state here as it needs to run regardless of the API outcome.
export async function quoteTrade(
  getAccessTokenSilently: Function,
  outgoingTradeMessage: any,
) {
  const sendQuoteResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Quote",
      method: "POST",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  return sendQuoteResponse ? sendQuoteResponse : false;
}
// 14868 - Revert code related to "/Trade/Execution/Recoupon" and ReCoupon flow.
// export async function recouponTradeAction({
//   data,
//   getAccessTokenSilently,
// }: {
//   data: ExecutionStrategy;
//   getAccessTokenSilently: Function;
// }) {
//   let numOfRecouponBlocks = 0;
//   let numOfDealerBlocks = 0;
//   let numOfCashBlocks = 0;

//   if (!data.legs || data.legs.length == 0) {
//     return;
//   }

//   data.legs.forEach((leg: ExecutionRequestQuoteLeg) => {
//     if (leg.unwind == "Recoupon") {
//       if (!leg.fees) {
//         return;
//       }
//       const currentTransactionObject = leg.fees!.find(
//         (feeObj) => feeObj.transactionFeeSide == leg.quoteSide,
//       );
//       if (!currentTransactionObject) {
//         return;
//       }
//       const currentTransactionFee = currentTransactionObject.transactionFee;
//       const currentIndicativeFee = currentTransactionObject.indicativeFee;
//       const dealer = returnFeeDirectionObjectKeyRPLeg({
//         status: data.status as ExecutionRequestStatus,
//         unwind: data.unwind,
//         legUnwind: leg.unwind,
//       });

//       if (!dealer) {
//         return;
//       }

//       numOfRecouponBlocks = numOfRecouponBlocks + 1;

//       // if (leg.dealer == null || leg.dealer == undefined) {
//       //   if (Math.sign(leg.indicativeFee) == -1) {
//       //     leg.dealer = "Pays";
//       //   } else if (Math.sign(leg.indicativeFee) == 1) {
//       //     leg.dealer = "Receives";
//       //   } else {
//       //     leg.dealer = "Pays";
//       //   }
//       // }

//       //Mitch Question - Sending back indicativeFee if there is no transactionFee
//       // if (leg.cash == null || leg.cash == undefined) {
//       //   leg.cash =
//       //     leg.quoteSide == "Bid"
//       //       ? leg.bidTransactionFee
//       //       : leg.quoteSide == "Ask"
//       //         ? leg.askTransactionFee
//       //         : "";

//       //   if (leg.cash == null || leg.cash == undefined) {
//       //     leg.cash = leg.indicativeFee;
//       //   }
//       // }
//       // if (leg.dealer && leg.dealer != "") {
//       numOfDealerBlocks = numOfDealerBlocks + 1;

//       if (leg.quoteSide == "Bid" && currentIndicativeFee) {
//         leg.bidTransactionFee =
//           currentTransactionObject[dealer] == "Pays"
//             ? Math.abs(
//                 currentTransactionFee
//                   ? currentTransactionFee
//                   : currentIndicativeFee,
//               ) * -1
//             : Math.abs(
//                 currentTransactionFee
//                   ? currentTransactionFee
//                   : currentIndicativeFee,
//               );
//       } else if (leg.quoteSide == "Ask" && currentIndicativeFee) {
//         leg.askTransactionFee =
//           currentTransactionObject[dealer] == "Pays"
//             ? Math.abs(
//                 currentTransactionFee
//                   ? currentTransactionFee
//                   : currentIndicativeFee,
//               ) * -1
//             : Math.abs(
//                 currentTransactionFee
//                   ? currentTransactionFee
//                   : currentIndicativeFee,
//               );
//       }

//       //  MITCH NOTE - Confusing here: Seems like if the old cash existed + 1 to numOfCashBlocks.
//       // Since the old cash could be either currentTransactionFee or currentIndicativeFee - Check both below.
//       if (
//         (currentTransactionFee && currentTransactionFee != 0) ||
//         (currentIndicativeFee && currentIndicativeFee != 0)
//       ) {
//         numOfCashBlocks = numOfCashBlocks + 1;
//       }
//     }
//     // if (leg.cash && leg.cash != 0) {
//     //   numOfCashBlocks = numOfCashBlocks + 1;
//     // }
//     // }
//   });

//   let recouponTradeObject = {
//     esid: data.esid,
//     executionRequest: {
//       executionRequestId: data.executionRequestId,
//     },
//     blocks: data.legs
//       .map((block: any) => {
//         if (block.unwind == "Recoupon") {
//           const feeBid: ISwapTradeFees = block.fees.find(
//             (feeObj: ISwapTradeFees) =>
//               feeObj.transactionFeeSide == QuoteSide.Bid,
//           );

//           const feeDealer = feeBid?.userUpdatedDirection
//             ? feeBid?.userUpdatedDirection
//             : feeBid?.transactionFee
//               ? feeBid?.transactionFeeDirection
//               : feeBid?.indicativeFeeDirection;

//           let bidTransactionFee;

//           if (feeBid?.transactionFee != null) {
//             bidTransactionFee =
//               feeDealer === "Pays"
//                 ? Number(feeBid?.transactionFee) * -1
//                 : feeBid?.transactionFee;
//           } else if (feeBid?.indicativeFee != null) {
//             bidTransactionFee =
//               feeDealer === "Pays"
//                 ? Number(feeBid?.indicativeFee) * -1
//                 : feeBid?.indicativeFee;
//           }
//           const feeAsk: ISwapTradeFees = block.fees.find(
//             (feeObj: ISwapTradeFees) =>
//               feeObj.transactionFeeSide == QuoteSide.Ask,
//           );
//           const askDealer = feeAsk?.userUpdatedDirection
//             ? feeAsk?.userUpdatedDirection
//             : feeAsk?.transactionFee
//               ? feeAsk?.transactionFeeDirection
//               : feeAsk?.indicativeFeeDirection;

//           let askTransactionFee;

//           if (feeAsk?.transactionFee != null) {
//             askTransactionFee =
//               askDealer === "Pays"
//                 ? Number(feeAsk?.transactionFee) * -1
//                 : feeAsk?.transactionFee;
//           } else if (feeAsk?.indicativeFee != null) {
//             askTransactionFee =
//               askDealer === "Pays"
//                 ? Number(feeAsk?.indicativeFee) * -1
//                 : feeAsk?.indicativeFee;
//           }

//           const feeMid: ISwapTradeFees = block.fees.find(
//             (feeObj: ISwapTradeFees) =>
//               feeObj.transactionFeeSide == QuoteSide.Mid,
//           );
//           const midDealer = feeMid?.userUpdatedDirection
//             ? feeMid?.userUpdatedDirection
//             : feeMid?.transactionFee
//               ? feeMid?.transactionFeeDirection
//               : feeMid?.indicativeFeeDirection;

//           let midTransactionFee;

//           if (feeMid?.transactionFee != null) {
//             midTransactionFee =
//               midDealer === "Pays"
//                 ? Number(feeMid?.transactionFee) * -1
//                 : feeMid?.transactionFee;
//           } else if (feeMid?.indicativeFee != null) {
//             midTransactionFee =
//               midDealer === "Pays"
//                 ? Number(feeMid?.indicativeFee) * -1
//                 : feeMid?.indicativeFee;
//           }
//           return {
//             blockId: block.blockId,
//             bidTransactionFee: bidTransactionFee,
//             askTransactionFee: askTransactionFee,
//             midTransactionFee: midTransactionFee,
//             size: block.quotedSize,
//           };
//           // return {
//           //   blockId: block.blockId,
//           //   bidTransactionFee: block.bidTransactionFee,
//           //   askTransactionFee: block.askTransactionFee,
//           //   midTransactionFee: block.midTransactionFee,
//           //   size: block.quotedSize,
//           // };
//         }
//         return undefined;
//       })
//       .filter((value: any) => value !== undefined),
//   };

//   if (
//     numOfDealerBlocks == numOfRecouponBlocks &&
//     numOfCashBlocks == numOfRecouponBlocks
//   ) {
//     let recouponSuccess = await recouponTrade(
//       getAccessTokenSilently,
//       recouponTradeObject,
//     );
//     if (recouponSuccess) {
//       // 14593 - RP to be notified they have recouponed the trade.
//       toast.success(`${data.receivingCustomer} has recouponed ${data.esid}.`); // MOVE TO LIB
//       setButtonBackToActive(data.esid);
//     }
//   } else {
//     toast.error("Select dealer and cash values for all Recoupon blocks ");
//     setButtonBackToActive(data.esid);
//   }
// }

export async function executeTradeStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: ExecutionStrategy;
  getAccessTokenSilently: Function;
}) {
  const structureType = data.structureType;
  const strategyType = data.strategyType;

  const tradeData = generateTradeJSON(data); // OBJ with both the quotes and trade from store.
  let sendSuccessMessage = false;
  let blocksQuoted = false;

  const unwind = data.unwind;
  let containsNPV = false;
  let containsRecoupon = false; //Mitch note: Not getting used.
  // Mitch note: Task 14200 could be related to above - task specifies not updating the transactionFees if the block is a recoupon.

  let earlyReturnDueToRecouponFeesAtNull = false;

  //NPV Style should only ever be a thing if all the blocks are:
  // Swaps AND ALL Swaps are flagged for NPV
  if (unwind == false && data.legs) {
    data.legs.forEach((leg: any) => {
      if (leg.unwind && leg.unwind == "NPV") {
        containsNPV = true;
      }
      if (leg.unwind && leg.unwind == "Recoupon") {
        containsRecoupon = true;
      }
    });
  }

  // LQC-590: Price notation ($p/$fr) blocks cannot be quoted at 0 - check first.
  const priceBlocksAtZero = returnPriceNotationBlocksQuotedAtZero(data);
  if (priceBlocksAtZero.length > 0) {
    generateToastMessageTradeData({
      action: "priceQuotedWith0",
      result: "error",
      ESIDPassed: data.esid,
    });
    triggerValidationFlash(priceBlocksAtZero);
    setButtonBackToActive(data.esid);
    return;
  }

  // LQC-889: Price notation ($p/$fr) blocks cannot be quoted negative.
  const priceBlocksNegative = returnPriceNotationBlocksQuotedNegative(data);
  if (priceBlocksNegative.length > 0) {
    generateToastMessageTradeData({
      action: "priceQuotedNegative",
      result: "error",
      ESIDPassed: data.esid,
    });
    triggerValidationFlash(priceBlocksNegative);
    setButtonBackToActive(data.esid);
    return;
  }

  // FUT blocks can NEVER be quoted with 0 - 14651 (only fires if price check above didn't)
  // No need to check this on UNWIND = true.
  if (unwind == false) {
    const futBlocksAtZero = returnFUTBlocksQuotedAtZero(data);
    if (futBlocksAtZero.length > 0) {
      generateToastMessageTradeData({
        action: "FUTQuotedWith0",
        result: "error",
        ESIDPassed: data.esid,
      });
      triggerValidationFlash(futBlocksAtZero);
      setButtonBackToActive(data.esid);
      return;
    }
  }

  if (unwind || (!unwind && containsNPV) || (!unwind && containsRecoupon)) {
    // RP - ER Level Dealer value
    const dealerER = returnDealerValueRPatER({ thisTrade: data });
    // Can return as "NotFound"
    // Capturing a value for validation of blocks.
    if (dealerER == "NotFound") {
    }
    let ERCashAmount: number | undefined | null = 0;
    if (data.fees && data.fees.length > 0) {
      const transactionFeeObject = data.fees.find(
        (feeObj) => feeObj.transactionFeeSide == data.quoteSideER,
      );
      const transactionFeeField = transactionFeeObject?.transactionFee;

      ERCashAmount =
        dealerER == "Pays"
          ? Math.abs(Number(transactionFeeField)) * -1
          : transactionFeeField;

      if (data.quoteSideER == "Bid") {
        if (dealerER == "Pays") {
          // ERCashAmount = Math.abs(Number(data.bid)) * -1;
          // Adjust the data to be a negative due to 'Pays' direction
          data.bid = Math.abs(Number(transactionFeeField)) * -1;
        } else if (dealerER == "Receives") {
          data.bid = Math.abs(Number(transactionFeeField)); // Still need to map the fee back onto the bid
          // ERCashAmount = Math.abs(Number(data.bid));
        }
        //  else
        // Will happen if dealerER returns as "NotFound"
        // toast.warning(
        //   "could not find dealer field on table data. Please select and ty again",
        // );
      } else if (data.quoteSideER == "Ask") {
        if (dealerER == "Pays") {
          // ERCashAmount = Math.abs(Number(data.ask)) * -1;
          // Adjust the data to be a negative due to 'Pays' direction
          data.ask = Math.abs(Number(transactionFeeField)) * -1;
        } else if (dealerER == "Receives") {
          data.ask = Math.abs(Number(transactionFeeField)); // Still need to map the fee back onto the ask
          // ERCashAmount = Math.abs(Number(data.ask));
        }
        // else
        // toast.warning(
        //  "could not find dealer field on table data. Please select and ty again",
        // );
      }
    }

    let blockCashSum = 0;
    if (data.legs) {
      data.legs.forEach((leg: ExecutionRequestQuoteLeg) => {
        // MITCH NOTE: Currently NOT capturing the bidTransactionFee or askTransactionFee values.
        // Will Replace this logic with the returnDealerValueRPatLeg function created for Quoting.

        if (leg.fees && leg.fees.length > 0) {
          const currentFeeObject = leg.fees!.find(
            (feeObj) => feeObj.transactionFeeSide == leg.quoteSide,
          );
          const dealer = returnDealerValueRPatLeg({
            data: leg,
            thisTrade: data,
            isOutright: data.strategyType == StrategyType.Outright,
          });

          const currentTransactionFee = currentFeeObject?.transactionFee;

          const backUpValue = leg.fees!.find(
            (feeObj) => feeObj.transactionFeeSide == leg.quoteSide,
          )?.indicativeFee; // Should 0 be here or JUST for display purposes (14300)

          // NOTE: DON'T allow the trade to be executed until there is a transaction fee. The user can click into the cash cell and accept the indicative fee or edit it. Clicking in will start to edit the indicative if it's populated.
          if (currentTransactionFee == null) {
            earlyReturnDueToRecouponFeesAtNull = true; // WILL TRIGGER Eearly return.
          }

          const legDirection = leg.quoteSide;
          if (legDirection == QuoteSide.Bid) {
            // BID
            // 14300 - If the quote side “legs” > “bidTransactionFee” or “askTransactionFee” is “null”: display the value in “legs” > “indicativeFee”
            if (currentTransactionFee != null) {
              leg.bidTransactionFee =
                dealer == "Pays"
                  ? (Math.abs(Number(currentTransactionFee)) * -1).toString()
                  : Math.abs(Number(currentTransactionFee)).toString();
            } else {
              // Using the Cube generated value as the user has not entered a value.
              leg.bidTransactionFee =
                dealer == "Pays"
                  ? (Math.abs(Number(backUpValue)) * -1).toString() // Fall back option
                  : Math.abs(Number(backUpValue)).toString(); // Fall back option
            }

            //Adjust the running total
            blockCashSum = blockCashSum + Number(leg.bidTransactionFee);
          } else if (legDirection == QuoteSide.Ask) {
            // ASK
            // 14300 - If the quote side “legs” > “bidTransactionFee” or “askTransactionFee” is “null”: display the value in “legs” > “indicativeFee”
            if (currentTransactionFee != null) {
              leg.askTransactionFee =
                dealer == "Pays"
                  ? (Math.abs(Number(currentTransactionFee)) * -1).toString()
                  : Math.abs(Number(currentTransactionFee)).toString();
            } else {
              // Using the Cube generated value as the user has not entered a value.
              leg.askTransactionFee =
                dealer == "Pays"
                  ? (Math.abs(Number(backUpValue)) * -1).toString() // Fall back option
                  : Math.abs(Number(backUpValue)).toString(); // Fall back option
            }
            //Adjust the running total
            blockCashSum = blockCashSum + Number(leg.askTransactionFee);
          }
        }

        // if (leg.unwind == "NPV") {
        //   let dealerLeg;
        //   if (leg.dealer == null || leg.dealer == undefined) {
        //     if (Math.sign(leg.indicativeFee) == -1) {
        //       dealerLeg = "Pays";
        //     } else if (Math.sign(leg.indicativeFee) == 1) {
        //       dealerLeg = "Receives";
        //     } else {
        //       dealerLeg = "Pays";
        //     }
        //   } else {
        //     dealerLeg = leg.dealer;
        //   }

        // let legCashValue = 0;
        // 14300 specifies to read askTransactionFee/bidTransactionFee first, indicativeFee as a back up and 0 as a catch all.

        // }
        // let userInput = leg.cash ? leg.cash : leg.indicativeFee;
        // if (dealerLeg == "Pays") {
        //   legCashValue = Math.abs(userInput) * -1;
        //   // blockCashSum = blockCashSum + Math.abs(leg.indicativeFee) * -1; //Math.abs(params.value)
        // } else if (dealerLeg == "Receives") {
        //   legCashValue = Math.abs(userInput);
        //   // blockCashSum = blockCashSum + Math.abs(leg.indicativeFee);
        // }
        // else
        //   toast.warning(
        //     "could not find dealer field on table data. Please select and ty again",
        //   );

        // blockCashSum = blockCashSum + legCashValue;

        // if (leg.quoteSide == "Bid") {
        //   leg.bidTransactionFee = legCashValue;
        // } else if (leg.quoteSide == "Ask") {
        //   leg.askTransactionFee = legCashValue;
        // }
      });
    }

    if (earlyReturnDueToRecouponFeesAtNull) {
      generateToastMessageTradeData({
        action: "noFeesOnRecoupon",
        result: "error",
        ESIDPassed: data.esid,
      });
      // LQC-207: Flash blocks with missing cash (transactionFee)
      triggerValidationFlash(returnBlocksWithMissingCash(data));
      setButtonBackToActive(data.esid);
      return;
    }

    // Regenerate the outgoing trade message as the previous trade message does not have the
    const npvTradeData = generateTradeJSON(data); // Replaced with the normal generateTradeFunciton transactionFees on it.
    // OBJ with both the quotes and trade from store.

    if (unwind) {
      if (Number(blockCashSum.toFixed(2)) == Number(ERCashAmount?.toFixed(2))) {
        const npvExecuteSuccess = await executeTrade(
          getAccessTokenSilently,
          npvTradeData,
        );
        if (npvExecuteSuccess) {
          // toast.success("Execute outright."); // MOVE TO LIB
          sendSuccessMessage = true;
        }
      } else {
        toast.error("Cash amounts do not sum to total");
      }
    } else {
      const npvExecuteSuccess = await executeTrade(
        getAccessTokenSilently,
        npvTradeData,
      );
      if (npvExecuteSuccess) {
        // toast.success("Execute outright."); // MOVE TO LIB
        sendSuccessMessage = true;
      }
    }
  } else if (
    // structureType == "OPT PACK" ||
    structureType == StructureTypeCode.SYNREPO ||
    structureType == StructureTypeCode.SYNREVREP
  ) {
    const canRepoTradeBeExecuted = executeSynRepo(tradeData);
    if (canRepoTradeBeExecuted) {
      sendSuccessMessage = await executeTrade(
        getAccessTokenSilently,
        tradeData,
      );
    }
  } else if (strategyType == StrategyType.Outright) {
    const canOutrightTradeBeExecuted = executeOutright(tradeData);
    if (canOutrightTradeBeExecuted) {
      const outrightExecuteSuccess = await executeTrade(
        getAccessTokenSilently,
        tradeData,
      );
      if (outrightExecuteSuccess) {
        // toast.success("Execute outright."); // MOVE TO LIB
        sendSuccessMessage = true;
      }
    }
  } else {
    // Handle pack logic after removing main know variables:
    const canStructureTradeBeExecuted = checkExecuteStructure(data); // send the whole trade through for the check.
    if (canStructureTradeBeExecuted) {
      blocksQuoted = true;
      const structureExecuteSuccess = await executeTrade(
        getAccessTokenSilently,
        tradeData,
      );
      if (structureExecuteSuccess) {
        // toast.success("Quoted structure"); // MOVE TO LIB
        sendSuccessMessage = true;
      }
    }
  }
  // Create toasts for
  if (sendSuccessMessage) {
    generateToastMessageTradeData({
      action: "executeTradeStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  } else {
    if (blocksQuoted) {
      generateToastMessageTradeData({
        action: "executeTradeStoreAction",
        result: "error",
        ESIDPassed: data.esid,
      });
    }
  }
  //Always make the button able to be cilcked at the end of the process.
  // May not catch on 'throw errors' - Also added to the fetchRequestWrapper

  setButtonBackToActive(data.esid);
}

function executeOutright(tradeJSONQuoted: any) {
  // DOES anything need to be done here? If trades connot have thier input values altered, nothnig to check?
  if (tradeJSONQuoted.blocks[0]?.quoteSide == QuoteSide.Bid) {
    if (
      tradeJSONQuoted.blocks[0]?.bid == undefined ||
      tradeJSONQuoted.blocks[0]?.bid == null
    ) {
      toast.error("Bid cannot be empty, requote required."); // change to lib
      triggerValidationFlash(
        returnMissingQuoteCellsFromTradeJSON(tradeJSONQuoted),
      );
      return false;
    } else {
      return true;
    }
  } else if (tradeJSONQuoted.blocks[0]?.quoteSide == QuoteSide.Ask) {
    if (
      tradeJSONQuoted.blocks[0]?.ask == undefined ||
      tradeJSONQuoted.blocks[0]?.ask == null
    ) {
      toast.error("Ask cannot be empty, requote required.");
      triggerValidationFlash(
        returnMissingQuoteCellsFromTradeJSON(tradeJSONQuoted),
      );
      return false;
    } else {
      return true;
    }
  }
}

function checkExecuteStructure(data: any): boolean {
  const areAllLegsQuoted = checkAllLegsAreQuotedDetailedResult(data);
  // TWO lots of logic:
  // Certain trade structures will be calculated by the cube, they can be sent to API with 1 block missing.
  // All other trade types NEED all blocks quoted.
  const listOfStructuresThatAutoCalculate = [
    // Swap block yield - future block yield.
    StructureTypeCode.BNDEFP,
    StructureTypeCode.SWPEFP,
    // Long tenor block yield - short tenor block yield.
    StructureTypeCode.BNDSWI,
    StructureTypeCode.SWPSPD,
    StructureTypeCode.SWPTFR,
    // 2 * medium tenor bond block yield - (long tenor bond block yield + Execution Request spread / 100)
    StructureTypeCode.BNDFLY,
    StructureTypeCode.SWPFLY,
    //  (Longest tenor block yield - Second longest tenor block yield) + (Third longest tenor block yield - Fourth longest tenor block yield)
    StructureTypeCode.BNDCON,
    StructureTypeCode.SWPCON,
  ];
  if (listOfStructuresThatAutoCalculate.includes(data.structureType)) {
    if (areAllLegsQuoted.amountOfBlocksLeftUnquoted > 1) {
      generateToastMessageForTradeFlowErrors({
        action: "executeStructure",
        outcome: "moreThan1BlocksNotQuoted",
      });
      triggerValidationFlash(returnMissingQuoteCellsFromLegs(data));
      return false;
    }
  }
  // All other trade types NEED all blocks quoted.
  else if (!areAllLegsQuoted.areAllBlocksQuoted) {
    generateToastMessageForTradeFlowErrors({
      action: "executeStructure",
      outcome: "allBlocksNotQuoted",
    });
    triggerValidationFlash(returnMissingQuoteCellsFromLegs(data));
    return false;
  }
  return true;
}

// QUOTING
export async function quoteTradeStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: ExecutionStrategy;
  getAccessTokenSilently: Function;
}) {
  const structureType = data.structureType;
  const strategyType = data.strategyType;
  const quoteType = data.quoteType;
  const direction = data.quoteSideER;
  const unwind = data.unwind;
  let containsNPV = false;

  //NPV Style should only ever be a thing if all the blocks are:
  // Swaps AND ALL Swaps are flagged for NPV
  if (unwind == false) {
    if (data.legs) {
      data.legs.forEach((leg: ExecutionRequestQuoteLeg) => {
        if (leg.unwind && leg.unwind == "NPV") {
          containsNPV = true;
        }
      });
    }
  }

  // LQC-590: Price notation ($p/$fr) blocks cannot be quoted at 0 - check first.
  const priceBlocksAtZero = returnPriceNotationBlocksQuotedAtZero(data);
  if (priceBlocksAtZero.length > 0) {
    generateToastMessageTradeData({
      action: "priceQuotedWith0",
      result: "error",
      ESIDPassed: data.esid,
    });
    triggerValidationFlash(priceBlocksAtZero);
    setButtonBackToActive(data.esid);
    return;
  }

  // LQC-889: Price notation ($p/$fr) blocks cannot be quoted negative.
  const priceBlocksNegative = returnPriceNotationBlocksQuotedNegative(data);
  if (priceBlocksNegative.length > 0) {
    generateToastMessageTradeData({
      action: "priceQuotedNegative",
      result: "error",
      ESIDPassed: data.esid,
    });
    triggerValidationFlash(priceBlocksNegative);
    setButtonBackToActive(data.esid);
    return;
  }

  // FUT blocks can NEVER be quoted with 0 - 14651 (only fires if price check above didn't)
  // No need to check this on UNWIND/NPV STYLE trades.
  if (unwind == false) {
    const futBlocksAtZero = returnFUTBlocksQuotedAtZero(data);
    if (futBlocksAtZero.length > 0) {
      generateToastMessageTradeData({
        action: "FUTQuotedWith0",
        result: "error",
        ESIDPassed: data.esid,
      });
      triggerValidationFlash(futBlocksAtZero);
      setButtonBackToActive(data.esid);
      return;
    }
  }

  const tradeData = generateTradeJSON(data);
  let additionalDataForMessage: string = ""; // to be used if there is more information to add to a toast message.

  let sendSuccessMessage = false;
  if (tradeData.executionRequest.expiryPeriod == 0) {
    // toastWithMessage("Please enter a valid time to 'Valid For'");
    setButtonBackToActive(data.esid);
    return;
  }
  // Unwind == true means the whole trade is NPV stlye

  // START OF NPV
  if (unwind == true || containsNPV) {
    if (unwind == true) {
      let preventQuoteDueToNPVSpreadNotQuoted = false;

      const dealer = returnDealerValueRPatER({ thisTrade: data });
      if (dealer == "NotFound") {
        toast("No dealer direction selected.");
        return;
      }

      if (!data.fees) {
        toast("No fees");
        return;
      }
      if (direction == QuoteSide.Bid) {
        // BID
        const transactionFeeObject = data.fees.find(
          (feeObj) => feeObj.transactionFeeSide == QuoteSide.Bid,
        );
        const transactionFeeField = transactionFeeObject?.transactionFee;

        if (transactionFeeField != null) {
          data.bid =
            dealer == "Pays"
              ? (Math.abs(Number(transactionFeeField)) * -1).toString()
              : Math.abs(Number(transactionFeeField)).toString();
        } else {
          if (unwind == true) {
            preventQuoteDueToNPVSpreadNotQuoted = true; // trigger early return.
          }
          data.bid = "";
        }
      } else if (direction == QuoteSide.Ask) {
        // ASK
        const transactionFeeObject = data.fees.find(
          (feeObj) => feeObj.transactionFeeSide == QuoteSide.Ask,
        );
        const transactionFeeField = transactionFeeObject?.transactionFee;
        if (transactionFeeField != null) {
          data.ask =
            dealer == "Pays"
              ? (Math.abs(Number(transactionFeeField)) * -1).toString()
              : Math.abs(Number(transactionFeeField)).toString();
        } else {
          if (unwind == true) {
            preventQuoteDueToNPVSpreadNotQuoted = true; // trigger early return.
          }
          data.ask = "";
        }
      }

      // Early return:
      if (preventQuoteDueToNPVSpreadNotQuoted) {
        toast.error("Missing NPV unwind amount.");
        const isOutright = data.strategyType == StrategyType.Outright;
        const erBlockId = isOutright
          ? data.legs?.[0]?.blockId
          : ER_LEVEL_FLASH_ID;
        if (erBlockId != null) {
          triggerValidationFlash([
            { esid: data.esid, blockId: erBlockId, field: "cash" },
          ]);
        }
        setButtonBackToActive(data.esid);
        return;
      }

      const reCalculatedTradeData = generateTradeJSON(data);
      const quoteNPVSuccess = await quoteTrade(
        getAccessTokenSilently,
        reCalculatedTradeData,
      );
      if (quoteNPVSuccess) {
        sendSuccessMessage = true;
      } else {
        // Might note be needed here.
        // setButtonBackToActive(data.esid);
      }
    }
    // NO mention on Recoupon as Recoupon mapping / values do not need to be looked at until the execution stage.
    else if (unwind == false && containsNPV) {
      // Task 14212 - When unmind = false
      let areAllNonNPVBlocksQuoted = true;
      let areAllNPVBlocksQuoted = true;
      let amountOfNonNPVBlocksLeftUnquoted = 0;
      // let amountOfNonNPVBlocks = 0; // Mitch note - Does not seem to get used.

      let spreadQuote = null;
      if (data.quoteSideER == "Bid") {
        if (data.fees && data.fees.length > 0) {
          spreadQuote = data.fees.find(
            (feeObj) => feeObj.transactionFeeSide == QuoteSide.Bid,
          )?.transactionFee;
        }
      }
      if (data.quoteSideER == "Ask") {
        if (data.fees && data.fees.length > 0) {
          spreadQuote = data.fees.find(
            (feeObj) => feeObj.transactionFeeSide == QuoteSide.Ask,
          )?.transactionFee;
        }
      }
      if (data.legs) {
        data.legs.forEach((leg: ExecutionRequestQuoteLeg) => {
          if (leg.unwind == "NPV") {
            // Mitch - TransactionFee should already be on TransactionFee
            // if (leg.cash == null || leg.cash == undefined) {
            //   if (leg.quoteSide == "Bid") {
            //     leg.cash = leg.bidTransactionFee;
            //   } else if (leg.quoteSide == "Ask") {
            //     leg.cash = leg.askTransactionFee;
            //   }

            //   if (leg.cash == null || leg.cash == undefined) {
            //     leg.cash = leg.indicativeFee;
            //   }
            // }

            // Mitch - Duplicated code to get a dealer field.
            // if (leg.dealer == null || leg.dealer == undefined) {
            //   if (leg.quoteSide == "Bid" && leg.bidTransactionFee) {
            //     if (Math.sign(data.bidTransactionFee) == -1) {
            //       leg.dealer = "Pays";
            //     } else if (Math.sign(data.bidTransactionFee) == 1) {
            //       leg.dealer = "Receives";
            //     }
            //   } else if (leg.quoteSide == "Ask" && leg.askTransactionFee) {
            //     if (Math.sign(data.askTransactionFee) == -1) {
            //       leg.dealer = "Pays";
            //     } else if (Math.sign(data.askTransactionFee) == 1) {
            //       leg.dealer = "Receives";
            //     }
            //   }

            //   if (leg.dealer == null || leg.dealer == undefined) {
            //     leg.dealer = "Pays";
            //   }
            // }

            // NEW CODE
            if (!leg.fees) {
              return;
            }

            const dealer = returnDealerValueRPatLeg({
              data: leg,
              thisTrade: data,
              isOutright: data.strategyType == StrategyType.Outright,
            });

            if (dealer == "NotFound") {
              return;
            }
            // else {
            // Continue once the dealer is found.
            const legSide = leg.quoteSide;
            const currentTransactionObject = leg.fees!.find(
              (feeObj) => feeObj.transactionFeeSide == legSide,
            ); // Can be mapped to either the big or ask transactionFeeFields
            if (!currentTransactionObject) {
              return;
            }
            const currentTransactionFee =
              currentTransactionObject.transactionFee; // Can be mapped to either the big or ask transactionFeeFields
            // Will neeed to consider how this is done for TWO WAY.

            if (legSide == QuoteSide.Bid) {
              // BID
              if (currentTransactionFee == null) {
                areAllNPVBlocksQuoted = false;
              } else {
                leg.bidTransactionFee =
                  dealer == "Pays"
                    ? (Math.abs(Number(currentTransactionFee)) * -1).toString()
                    : Math.abs(Number(currentTransactionFee)).toString();
              }
            } else if (legSide == QuoteSide.Ask) {
              // ASK
              if (currentTransactionFee == null) {
                areAllNPVBlocksQuoted = false;
              } else {
                leg.askTransactionFee =
                  dealer == "Pays"
                    ? (Math.abs(Number(currentTransactionFee)) * -1).toString()
                    : Math.abs(Number(currentTransactionFee)).toString();
              }
            }
            // }
            // END NEW CODE

            // if (
            //   leg.cash == null ||
            //   leg.cash == undefined ||
            //   leg.dealer == null ||
            //   leg.dealer == undefined
            // ) {
            //   areAllNPVBlocksQuoted = false;
            // } else {
            //   if (leg.quoteSide == "Bid") {
            //     if (leg.dealer == "Pays") {
            //       leg.bidTransactionFee = Math.abs(leg.cash) * -1;
            //     } else if (leg.dealer == "Receives") {
            //       leg.bidTransactionFee = Math.abs(leg.cash);
            //     }
            //   } else if (leg.quoteSide == "Ask") {
            //     if (leg.dealer == "Pays") {
            //       leg.askTransactionFee = Math.abs(leg.cash) * -1;
            //     } else if (leg.dealer == "Receives") {
            //       leg.askTransactionFee = Math.abs(leg.cash);
            //     }
            //   }
            // }
          } else {
            // amountOfNonNPVBlocks++; // Mitch - Seems to be a duplicate for the NORMAL Quote side validation.
            // NOTE: When quoting a NPV block, if the block is NOT a NPV, it must still have it's regular bid or ask quoted.
            if (leg.quoteSide == "Bid") {
              if (leg.bid == undefined || leg.bid == null || leg.bid == "") {
                amountOfNonNPVBlocksLeftUnquoted++;
                areAllNonNPVBlocksQuoted = false;
              }
            } else if (leg.quoteSide == "Ask") {
              if (leg.ask == undefined || leg.ask == null || leg.ask == "") {
                amountOfNonNPVBlocksLeftUnquoted++;
                areAllNonNPVBlocksQuoted = false;
              }
            }
          }
        });
      }
      //ERROR - Early Returns
      // Mitch NOTE - This validation is stil Requierd.
      let sendQuote = true;
      let toastErrorThrown = false;
      if (!areAllNPVBlocksQuoted) {
        if (!toastErrorThrown) {
          toast.error("Missing NPV unwind amounts.");
          toastErrorThrown = true;
        }
        sendQuote = false;
      }
      if (!areAllNPVBlocksQuoted) {
        if (!spreadQuote && amountOfNonNPVBlocksLeftUnquoted > 0) {
          sendQuote = false;
          if (!toastErrorThrown) {
            toastErrorThrown = true;
            toast.error("Please quote the spread or all blocks.");
          }
        }
      }
      if (!areAllNonNPVBlocksQuoted) {
        if (!(amountOfNonNPVBlocksLeftUnquoted == 1 && spreadQuote)) {
          if (!toastErrorThrown) {
            toast.error("Please quote the spread or all blocks.");

            toastErrorThrown = true;
          }
          sendQuote = false;
        }
      }
      // Mitch NOTE: May not be any need to have a dedicated generateNPVTradeJSON - Can's see a purpose to it.

      // LQC-207: Flash missing cash cells AND missing quote cells (excluding NPV legs which don't need bid/ask)
      if (!sendQuote) {
        const missingCash = returnBlocksWithMissingCash(data);
        const missingQuotes = returnMissingQuoteCellsFromLegs(data, ["NPV"]);
        triggerValidationFlash([...missingCash, ...missingQuotes]);
      }

      if (sendQuote) {
        // let partialNPVBlock = generateNPVTradeJSON(data); // NO LONGER USED
        // Recalculate TradeData
        const reCalculatedTradeData = generateTradeJSON(data);

        const quoteNPVSuccess = await quoteTrade(
          getAccessTokenSilently,
          reCalculatedTradeData,
        ); // Data has been adjusted for + / - values
        if (quoteNPVSuccess) {
          sendSuccessMessage = true;
        }
      } else {
        setButtonBackToActive(data.esid);
      }
    }
    // END OF NPV
  } else if (
    // structureType == "OPT PACK" ||
    structureType == StructureTypeCode.SYNREPO ||
    structureType == StructureTypeCode.SYNREVREP
  ) {
    let canRepoTradeBeQuoted = false;
    if (quoteType == "Two-Way") {
      canRepoTradeBeQuoted = quoteValidationTwoWayRepo(tradeData);
    } else {
      canRepoTradeBeQuoted = quoteSynRepo(tradeData);
    }
    if (canRepoTradeBeQuoted) {
      const quoteSynRepoQuoteSuccess = await quoteTrade(
        getAccessTokenSilently,
        tradeData,
      );
      if (quoteSynRepoQuoteSuccess) {
        sendSuccessMessage = true;
      }
    }
  } else if (strategyType == StrategyType.Outright) {
    let canOutrightTradeBeQuoted = false;
    if (quoteType == "Two-Way") {
      canOutrightTradeBeQuoted = quoteValidationTwoWayOutright(tradeData);
    } else canOutrightTradeBeQuoted = quoteOutright(tradeData);

    if (canOutrightTradeBeQuoted) {
      const outrightQuoteSuccess = await quoteTrade(
        getAccessTokenSilently,
        tradeData,
      );
      if (outrightQuoteSuccess) {
        sendSuccessMessage = true;
      }
    }
  } else {
    // Handle pack logic after removing main know variables:
    let canStructureTradeBeQuoted = false;
    if (quoteType == "Two-Way") {
      if (direction == "Two-Way") {
        // Indication that direction was set to "Withheld"
        canStructureTradeBeQuoted =
          quoteValidationTwoWayStructureDirectionWithheld(tradeData);
      } else {
        // if there is a direction - Assume the Direction was "Shown", not set to "Withheld"
        canStructureTradeBeQuoted =
          quoteValidationTwoWayStructureDirectionShown(tradeData);
      }
    } else {
      canStructureTradeBeQuoted = quoteStructure(tradeData);
    }

    if (canStructureTradeBeQuoted) {
      const structureQuoteSuccessMessage = await quoteTrade(
        // returning an object if passing or false if failing.
        getAccessTokenSilently,
        tradeData,
      );
      if (structureQuoteSuccessMessage) {
        sendSuccessMessage = true;
        additionalDataForMessage = structureQuoteSuccessMessage.message;
      }
    }
  }
  if (sendSuccessMessage) {
    generateToastMessageTradeData({
      action: "quoteTradeStoreAction",
      result: "success",
      ESIDPassed: data.esid,
      additionalData:
        additionalDataForMessage != "" && additionalDataForMessage, // If the spread is recalculated, a message should be returned on the trade message.
    });

    const hasLegWithSizeChangeButNoBidAsk = data.legs?.some(
      (leg: ExecutionRequestQuoteLeg) =>
        legHasReducedQuotedSize(leg) &&
        (leg.bid == null || leg.bid === "") &&
        (leg.ask == null || leg.ask === ""),
    );
    if (hasLegWithSizeChangeButNoBidAsk) {
      generateMissingQuotesForQuotedSizeNotification()
    }
  }
  // Might not be getting hit if an error occurs on the Quote trade function specifically.
  setButtonBackToActive(data.esid);
}

interface IUpdateSettlementMessage {
  esid: string;
  executionRequestId: string;
  blockid: string;
  effectiveDate: string;
}

export async function updateSettlementDate(
  esid: string,
  erid: string,
  blockid: string,
  date: string,
  getAccessTokenSilently: Function,
) {
  const outgoingTradeMessage: IUpdateSettlementMessage = {
    esid: esid,
    executionRequestId: erid,
    blockid: blockid,
    effectiveDate: date,
  };
  const updateSettlement = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Edit/Settlement",
      method: "PATCH",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (updateSettlement) {
    generateToastMessageTradeData({
      action: "updateSettlement",
      result: "success",
      ESIDPassed: esid,
    });
  } else {
    generateToastMessageTradeData({
      action: "updateSettlement",
      result: "error",
      ESIDPassed: esid,
    });
  }
}

interface IUpdateStriketMessage {
  esid: string;
  executionRequestId: string;
  blockid: string;
  dealtStrike?: number; // check this as not ALL strikes map to 'dealtStrike'
  deliveryDate?: string;
  cpi?: number;
}
export async function updateExecutionEditSecurity({
  esid,
  erid,
  blockid,
  deliveryDate,
  dealtStrike,
  cpi,
  getAccessTokenSilently,
}: {
  esid: string;
  erid: string;
  blockid: string;
  deliveryDate?: string;
  dealtStrike?: number;
  cpi?: number;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: IUpdateStriketMessage = {
    esid: esid,
    executionRequestId: erid,
    blockid: blockid,
    // deliveryDate: deliveryDate,
    // dealtStrike: dealtStrike, // check this as not ALL strikes map to 'dealtStrike'
  };
  if (deliveryDate) {
    outgoingTradeMessage.deliveryDate = deliveryDate;
  }
  if (dealtStrike) {
    outgoingTradeMessage.dealtStrike = dealtStrike;
  }
  if (cpi) {
    outgoingTradeMessage.cpi = cpi;
  }

  const updateStrike = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Edit/Security",
      method: "PATCH",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (updateStrike) {
    generateToastMessageTradeData({
      action: "updateSecurity",
      result: "success",
      ESIDPassed: esid,
    });
  } else {
    generateToastMessageTradeData({
      action: "updateSecurity",
      result: "error",
      ESIDPassed: esid,
    });
  }
}

// GENERERIC functions to update data in the store to keep tables in sync.
export function updateQuotesAcrossTables(params: any) {
  const currentTradeData = tradeDataStore.getState().tradeData;
  const updatedTradeData = currentTradeData.map((trade) => {
    if (trade.esid == params.data.esid) {
      return { ...params.data };
    } else return trade;
  });
  tradeDataStore.getState().updateTradeData(updatedTradeData);
}

// LQC-922 — optimistic per-CP releaseSize write fired from the Iceberg cell
// editor before its PATCH. MegaPipe pushes the authoritative value back through
// the regular trade-data update path; this just removes the visible lag between
// the user pressing Enter and the cell reflecting the new percent.
// `releaseSize` is the decimal (0-1) — same shape the API/MegaPipe use.
export function updateCPReleaseSizeInStore({
  esid,
  executionRequestId,
  releaseSize,
}: {
  esid: string;
  executionRequestId: number | string;
  releaseSize: number;
}) {
  const currentTradeData = tradeDataStore.getState().tradeData;
  const updatedTradeData = currentTradeData.map((trade: any) => {
    if (trade.esid != esid) return trade;
    const cps = trade.executionRequestCounterParties;
    if (!cps) return trade;
    const updatedCPs = cps.map((cp: any) =>
      cp.executionRequestId == executionRequestId
        ? { ...cp, releaseSize }
        : cp,
    );
    return { ...trade, executionRequestCounterParties: updatedCPs };
  });
  tradeDataStore.getState().updateTradeData(updatedTradeData);
}

export function updateTradeDataAcrossTablesAtBlockLevel(
  params: any,
  esid: string,
) {
  const currentTradeData = tradeDataStore.getState().tradeData;
  const updatedTradeData = currentTradeData.map((trade: ExecutionStrategy) => {
    if (trade.esid == esid) {
      const newParamsToReturn: ExecutionStrategy = { ...trade };
      if (newParamsToReturn.legs) {
        newParamsToReturn.legs.map((leg) => {
          // map over the legs and replace the matching block id with tha data passed in at params.
          if (leg.blockId == params.blockId) {
            return { ...params };
          } else {
            return leg;
          }
        });
        return newParamsToReturn;
      } else return newParamsToReturn; // more logic here for the OP using this funciton.
    } else return trade;
  });
  tradeDataStore.getState().updateTradeData(updatedTradeData);
}

export async function cancelInOMSStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const esid = data.esid;
  const blockIds = data.legs.reduce((acc: string[], current: any) => {
    acc.push(current.blockId);
    return acc;
  }, []);

  const outgoingTradeMessage = {
    esid: esid,
    blockIds: blockIds,
  };

  const cancelTradeResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Cancel",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (cancelTradeResponse) {
    generateToastMessageTradeData({
      action: "cancelInOMSStoreAction",
      result: "success",
      ESIDPassed: esid,
    });
  }
}

export async function recreateBlocksStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  // const blockObjects = data.legs.reduce((acc: any[], current: any) => {
  //   acc.push({
  //     id: current.blockId,
  //     allocations: [
  //       //TODO:  Curretly don't have access to the allocations details.
  //       {
  //         // id: 0,
  //         include: true,
  //       },
  //     ],
  //   });
  //   return acc;
  // }, []);

  const outgoingTradeMessage = {
    esid: data.esid,
  };

  const recreateBlocksResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Recreate",
      method: "Post",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (recreateBlocksResponse) {
    generateToastMessageTradeData({
      action: "recreateBlocksStoreAction",
      result: "success",
    });
  }
}

// LQC-2576 - re-creates the trade's blocks in the reversed direction so the risk
// can be offered out as unassigned blocks in Trade Creation. The caller resolves the
// ER id (widget helpers must not be imported into this file - see CLAUDE.md cycles).
// LQC-2847 - the OP omits the ER id; an RP without one would hit the OP path.
export async function offloadRiskStoreAction({
  data,
  executionRequestId,
  getAccessTokenSilently,
}: {
  data: any;
  executionRequestId?: number;
  getAccessTokenSilently: Function;
}) {
  if (data?.role == "RP" && executionRequestId == undefined) return;

  const offloadRiskResponse = await fetchRequestWrapper(
    {
      url: "/Trade/OffloadRisk",
      method: "Post",
      objToSend: { esid: data.esid, executionRequestId },
    },
    getAccessTokenSilently,
  );

  if (!offloadRiskResponse) return;

  if (offloadRiskResponse.blockIds?.length === 0) {
    toast.info(`${data.esid} has no blocks to off-load`);
    return;
  }

  generateToastMessageTradeData({
    action: "offloadRiskStoreAction",
    result: "success",
    ESIDPassed: data.esid,
  });
  // Pull, as Trade From Scratch does - don't rely on MegaPipe's TradeImported alone.
  await refreshUnassignedBlocks(getAccessTokenSilently);
}
